import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/ui";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { BuildInfo } from "@/components/layout/BuildInfo";

// The three product promises surfaced on the sign-in brand panel, each with a
// teal check. Trilingual: labels come from i18n, never hardcoded here.
const FEATURES = ["login.feature1", "login.feature2", "login.feature3"];

// Headline social-proof stats. Values are display strings (already comma-formatted
// per house style) so they stay in the locale file alongside their labels.
const STATS = [
  { value: "login.stat1Value", label: "login.stat1Label" },
  { value: "login.stat2Value", label: "login.stat2Label" },
  { value: "login.stat3Value", label: "login.stat3Label" },
];

// BrandShell is the left panel frame every auth flow shares: the gradient backdrop
// with a soft accent glow in the top-left, generous padding, and top/bottom
// anchoring (justify-between). Each flow supplies its own content and picks the
// glow accent - amber for sign-in, red for first-time setup.
export function BrandShell({
  glow = "amber",
  children,
}: {
  glow?: "amber" | "red" | "teal" | "platform";
  children: ReactNode;
}) {
  const color =
    glow === "red"
      ? "var(--color-red)"
      : glow === "teal"
        ? "var(--color-teal)"
        : glow === "platform"
          ? "var(--color-platform)"
          : "var(--color-amber)";
  return (
    <div
      className="flex h-full w-full flex-col justify-between p-14 short:p-11 shorter:p-8"
      style={{
        backgroundImage:
          `radial-gradient(90% 75% at 18% 12%, color-mix(in oklab, ${color} 15%, transparent), transparent 60%), ` +
          "linear-gradient(135deg, var(--color-surface), var(--color-sidebar))",
      }}
    >
      {children}
    </div>
  );
}

// BrandLockup is the mark + wordmark shown top-left on every auth panel. Platform
// panels pass a teal/purple `accent` mark, a "Klutch Platform" label, and a chip.
export function BrandLockup({
  accent,
  label = "Klutch",
  chip,
}: {
  accent?: string;
  label?: string;
  chip?: ReactNode;
} = {}) {
  return (
    <div className="flex items-center gap-3">
      <Logo size={30} withWordmark={false} accent={accent} />
      <div className="flex items-center gap-2.5">
        <span className="font-head text-[22px] font-bold text-ink">{label}</span>
        {chip}
      </div>
    </div>
  );
}

// LoginBrand is the marketing panel for the sign-in screens: eyebrow, headline,
// tagline, the feature list and the social-proof stats. It is the default brand
// panel; setup/2FA flows pass their own via AuthShell's `brand` prop.
export function LoginBrand() {
  const { t } = useTranslation();
  return (
    <BrandShell glow="amber">
      <BrandLockup />

      <div className="flex max-w-xl flex-col gap-5">
        <span className="font-mono text-xs tracking-[0.14em] text-amber-ink">
          {t("login.eyebrow")}
        </span>
        <h1 className="max-w-130 font-head text-[44px] font-bold leading-[1.1] tracking-tight text-ink">
          {t("login.headline")}
        </h1>
        <p className="max-w-115 text-[16px] leading-relaxed text-muted">{t("login.tagline")}</p>

        <ul className="flex flex-col gap-3 pt-2">
          {FEATURES.map((key) => (
            <li key={key} className="flex items-center gap-3 text-[15px] text-ink-2">
              <span className="grid size-5.5 shrink-0 place-items-center rounded-full bg-teal-bg text-teal-ink">
                <Check size={14} strokeWidth={2.5} />
              </span>
              {t(key)}
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-4 pt-4">
          <div className="h-px w-full bg-edge-2" />
          <div className="flex gap-7">
            {STATS.map(({ value, label }) => (
              <div key={value} className="flex flex-col gap-1">
                <div className="font-head text-[26px] font-bold text-ink">{t(value)}</div>
                <div className="text-[13px] text-muted">{t(label)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2.5 text-[13px] text-muted-2">
        <ShieldCheck size={16} />
        {t("login.secureNote")}
      </div>
    </BrandShell>
  );
}

// AuthShell is the two-column frame shared by every unauthenticated page. Left: a
// brand panel (defaults to LoginBrand; pages pass their own via `brand`), hidden
// below lg so the form owns small screens. Right: a fixed-width form panel the
// page drops its form into via `children`, with the theme + language toggles
// top-right (kept even though the .pen mockups omit them, because a trilingual
// login must let a Sinhala/Tamil user switch language before signing in).
export function AuthShell({ children, brand }: { children: ReactNode; brand?: ReactNode }) {
  return (
    // Locked to the viewport height so the page itself never scrolls (a hard
    // requirement). On a short window the form column tightens its spacing and
    // control height (44px, or 40px with a mouse - touch keeps a 44px target) so
    // the form still fits; only past that does the form column scroll internally.
    <div className="flex h-dvh overflow-hidden bg-bg">
      <div className="relative hidden flex-1 overflow-hidden lg:block">{brand ?? <LoginBrand />}</div>

      <div className="flex h-full w-full flex-col bg-bg short:[--ctl-h:2.75rem] pointer-fine:shorter:[--ctl-h:2.5rem] lg:w-150 lg:flex-none">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-6 sm:px-14 short:pt-4 shorter:pt-3">
          <div className="flex shrink-0 items-center justify-end gap-2.5">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <div className="mx-auto flex w-full max-w-100 flex-1 flex-col justify-center py-6 short:py-3 shorter:py-1">
            {children}
          </div>
        </div>
        {/* Its own row below the scroll area, not floated over it: an absolutely
            placed stamp sat on top of the form's last lines on any tall form. */}
        <div className="flex shrink-0 justify-end px-6 pb-4 pt-1 sm:px-14 short:pb-3 shorter:pb-2">
          <BuildInfo />
        </div>
      </div>
    </div>
  );
}
